import styled from 'styled-components';

export const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 1rem;

  @media (min-width: 768px) {
    padding: 2rem;
  }

  @media (min-width: 1024px) {
    max-width: 1200px;
  }
`;

export const PageHeader = styled.header`
  margin-bottom: 2rem;
  text-align: center;

  @media (min-width: 768px) {
    text-align: left;
  }
`;

export const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.secondary};
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;

  @media (min-width: 768px) {
    font-size: 2rem;
  }

  @media (min-width: 1024px) {
    font-size: 2.5rem;
  }
`;

export const PageSubtitle = styled.p`
  color: #64748b;
  font-size: 1rem;
  line-height: 1.6;

  @media (min-width: 768px) {
    font-size: 1.1rem;
  }
`;

export const Card = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
`;

export const CardHeader = styled.header`
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

export const CardTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

export const CardDescription = styled.p`
  color: #64748b;
  font-size: 0.85rem;
  line-height: 1.5;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

export const CardContent = styled.div`
  padding: 1rem;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

export const Button = styled.button`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 2.5rem;
  cursor: pointer;
  border: none;
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
    padding: 0.75rem 1.5rem;
    font-size: 0.95rem;
    min-height: 2.75rem;
  }
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background-color: #2563eb;
          color: white;
          &:hover {
            background-color: #1d4ed8;
            transform: translateY(-1px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
        `;
      case 'success':
        return `
          background-color: #10b981;
          color: white;
          &:hover {
            background-color: #059669;
            transform: translateY(-1px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
        `;
      case 'danger':
        return `
          background-color: #ef4444;
          color: white;
          &:hover {
            background-color: #dc2626;
            transform: translateY(-1px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          color: #2563eb;
          border: 1px solid #2563eb;
          &:hover {
            background-color: #2563eb;
            color: white;
          }
        `;
      default:
        return `
          background-color: #f1f5f9;
          color: #1e293b;
          border: 1px solid #e2e8f0;
          &:hover {
            background-color: #e2e8f0;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }

  ${props => props.size === 'sm' && `
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    min-height: 2rem;

    @media (min-width: 768px) {
      padding: 0.5rem 1rem;
      font-size: 0.85rem;
    }
  `}
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;

  @media (min-width: 640px) {
    font-size: 0.8rem;
  }

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }

  @media (min-width: 1024px) {
    font-size: 1rem;
  }
`;

export const TableHeader = styled.thead`
  background-color: #f8fafc;

  @media (max-width: 1023px) {
    display: none;
  }
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid #e2e8f0;
  
  &:hover {
    background-color: #f8fafc;
  }

  @media (max-width: 1023px) {
    display: block;
    margin-bottom: 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    padding: 1rem;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const TableHeaderCell = styled.th`
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #1e293b;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;

  @media (min-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 1023px) {
    display: none;
  }
`;

export const TableCell = styled.td`
  padding: 0.75rem;
  color: #64748b;
  vertical-align: middle;

  @media (min-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 1023px) {
    display: block;
    padding: 0.5rem 0;
    border: none;
    text-align: left;
    
    &:before {
      content: attr(data-label);
      font-weight: 600;
      color: #1e293b;
      display: inline-block;
      width: 100px;
      margin-right: 0.75rem;
      font-size: 0.8rem;
    }
  }

  @media (max-width: 639px) {
    padding: 0.25rem 0;
    
    &:before {
      width: 80px;
      margin-right: 0.5rem;
      font-size: 0.75rem;
    }
  }
`;

export const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  display: inline-block;

  @media (min-width: 768px) {
    font-size: 0.8rem;
  }
  
  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background-color: rgba(16, 185, 129, 0.1);
          color: #10b981;
        `;
      case 'inactive':
        return `
          background-color: rgba(100, 116, 139, 0.1);
          color: #64748b;
        `;
      case 'pending':
        return `
          background-color: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        `;
      case 'outbid':
        return `
          background-color: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        `;
      case 'closed':
        return `
          background-color: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        `;
      case 'money_out':
        return `
          background-color: rgba(220, 38, 38, 0.1);
          color: #dc2626;
        `;
      default:
        return `
          background-color: #f8fafc;
          color: #64748b;
        `;
    }
  }}
`;

export const Grid = styled.div`
  display: grid;
  gap: 1rem;
  
  @media (min-width: 768px) {
    gap: 2rem;
  }
  
  ${props => {
    switch (props.columns) {
      case 2:
        return `
          grid-template-columns: 1fr;
          @media (min-width: 768px) {
            grid-template-columns: repeat(2, 1fr);
          }
          @media (min-width: 1024px) {
            grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          }
        `;
      case 3:
        return `
          grid-template-columns: 1fr;
          @media (min-width: 640px) {
            grid-template-columns: repeat(2, 1fr);
          }
          @media (min-width: 1024px) {
            grid-template-columns: repeat(3, 1fr);
          }
        `;
      default:
        return 'grid-template-columns: 1fr;';
    }
  }}
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.2s ease;
  padding: 1rem;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @media (max-width: 767px) {
    align-items: flex-end;
    padding: 0;
  }
`;

export const Modal = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from { 
      opacity: 0;
      transform: translateY(-20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 767px) {
    max-width: 100%;
    border-radius: 0.75rem 0.75rem 0 0;
    margin-top: auto;
  }
`;

export const ModalHeader = styled.header`
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

export const ModalTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

export const ModalContent = styled.div`
  padding: 1rem;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

export const ModalFooter = styled.footer`
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  flex-direction: column;

  @media (min-width: 768px) {
    padding: 1.5rem;
    flex-direction: row;
    gap: 1rem;
  }
`;

export const LoadingSpinner = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: #64748b;

  @media (min-width: 768px) {
    padding: 3rem;
  }

  .empty-icon {
    font-size: 2.5rem;
    margin-bottom: 1rem;

    @media (min-width: 768px) {
      font-size: 3rem;
    }
  }

  .empty-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #1e293b;

    @media (min-width: 768px) {
      font-size: 1.5rem;
    }
  }

  .empty-description {
    font-size: 0.9rem;
    color: #64748b;
    line-height: 1.6;

    @media (min-width: 768px) {
      font-size: 1rem;
    }
  }
`;

export const ItemSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background-color: #f8fafc;
  margin-bottom: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 1.5rem;
  }

  .item-info {
    flex: 1;
  }

  .item-name {
    font-weight: 600;
    font-size: 1rem;
    color: #1e293b;
    margin-bottom: 0.25rem;

    @media (min-width: 768px) {
      font-size: 1.1rem;
    }
  }

  .item-details {
    color: #64748b;
    font-size: 0.85rem;

    @media (min-width: 768px) {
      font-size: 0.9rem;
    }
  }

  .draw-button {
    @media (min-width: 768px) {
      margin-left: 1rem;
    }
  }
`; 